/**
 * Clicks that leave the page: where they went, and why measuring them cannot
 * be allowed to interfere with them.
 *
 * ## The rule this whole file is arranged around
 *
 * **The link wins.** A visitor clicking through to a partner site, or clicking
 * a price list, is doing the thing the customer put the link there for. This
 * script is a bystander, and a bystander that delays, cancels or breaks that
 * navigation has cost the customer the exact conversion they installed us to
 * count. So:
 *
 *  - `preventDefault` is never called, and there is no `setTimeout` waiting for
 *    a beacon before letting the browser proceed. That pattern — used by more
 *    than one analytics vendor — makes every outbound click 100–150 ms slower
 *    and turns a blocked request into a hung link;
 *  - the measurement leaves on `navigator.sendBeacon`, which the browser owns
 *    and which survives the document being torn down. That is the property that
 *    makes not waiting safe, and it is why `transport.ts` prefers it;
 *  - the listener is registered in the CAPTURE phase, so it still runs when the
 *    page's own handler calls `stopPropagation()` — which routers, modal
 *    libraries and consent managers do constantly;
 *  - every path through here is inside a `try`. An exception thrown from a
 *    capture-phase document listener lands in the customer's console on their
 *    own site, attached to a click they will report as their bug.
 *
 * ## What is counted, and what is refused
 *
 * A click is measured when the link goes to another host (outbound) or ends in
 * a file extension (a download). Nothing else: a same-host page link is
 * ordinary navigation and is already counted as the page view it produces.
 *
 * Schemes other than `http` and `https` are refused outright. `mailto:` is the
 * one that matters — its interesting half is an e-mail address, and a product
 * that drops the query string to avoid storing a password-reset token is not
 * going to store an address because it arrived in a different part of the URL.
 * `tel:` is the same argument, and `javascript:` is not a destination at all.
 */
import {
  DOWNLOAD_EVENT_NAME,
  DOWNLOAD_EXTENSION_PATTERN,
  MAX_DESTINATION_LENGTH,
  MAX_PATH_LENGTH,
  OUTBOUND_EVENT_NAME,
  scrubFilePath,
} from '@panel/shared/analytics';

export interface LinkHit {
  /** `OUTBOUND_EVENT_NAME` or `DOWNLOAD_EVENT_NAME`. */
  readonly name: string;
  /** Host plus scrubbed path, `instagram.com/emnashop`. */
  readonly destination: string;
}

/**
 * What a click on this URL is worth measuring as, or null.
 *
 * Pure, and separated from the listener below so the unit suite can put a
 * hundred URLs through the decision without a DOM. The listener is the part
 * that needs a real browser and has a test of its own in `browser/`.
 *
 * ## A file link to another host is a download, not an outbound click
 *
 * Both are true of `partner.example/catalogue.pdf`, and sending two events for
 * one click would double the click in every total the dashboard draws. The
 * download wins because it is the more specific fact and because it loses
 * nothing: the destination it stores is host-qualified, so the partner is still
 * visible in the row. The reverse choice would have thrown the extension away.
 */
export function linkHit(url: URL, loc: Location): LinkHit | null {
  const protocol = url.protocol;
  if (protocol !== 'http:' && protocol !== 'https:') return null;

  const host = url.hostname;
  if (!host) return null;

  // `url.pathname`, which never carries a query string, so `/pricing?x=.pdf`
  // is a page and not a download. `scrubFilePath` is what keeps the extension
  // on a filename the redaction collapsed — see its header for the bug that
  // made it necessary.
  const path = scrubFilePath(url.pathname, MAX_PATH_LENGTH);
  const download = DOWNLOAD_EXTENSION_PATTERN.test(path);
  if (!download && host === loc.hostname) return null;

  return {
    name: download ? DOWNLOAD_EVENT_NAME : OUTBOUND_EVENT_NAME,
    // A bare `/` is dropped rather than glued on: `instagram.com` and
    // `instagram.com/` are one destination to a reader and two rows to a
    // `GROUP BY`, and the apex link is the single most common outbound link
    // there is.
    destination: (host + (path === '/' ? '' : path)).slice(0, MAX_DESTINATION_LENGTH),
  };
}

/**
 * Watch the document for clicks on links, and hand each measurable one over.
 *
 * Returns the detach function, which `stop()` calls — the browser suite loads
 * several pages in one context and a listener left on a torn-down document is
 * how a test starts measuring the previous test's page.
 *
 * ## Two event types, and the button check on the second
 *
 * `click` covers the primary button and the keyboard, which is nearly all of
 * it. `auxclick` is the middle button — "open in a new tab" — which on an
 * outbound link is not a rare gesture but the NORMAL one, because the visitor
 * wants to keep the customer's page open. Missing it would undercount exactly
 * the clicks that did not cost the customer the session.
 *
 * `auxclick` also fires for the right button in engines that implement it
 * fully, and opening a context menu is not a click-through — hence the explicit
 * `button !== 1`.
 */
export function watchLinks(win: Window, onHit: (hit: LinkHit) => void): () => void {
  const doc = win.document;

  const handle = (event: Event): void => {
    try {
      if (event.type === 'auxclick' && (event as MouseEvent).button !== 1) return;
      const target = event.target as Element | null;
      // `closest` and not the target itself: the clickable thing inside a link
      // is almost always a `<span>`, an `<img>` or an icon, and the target is
      // whichever of those the pointer was over. Guarded because a click whose
      // target is the document or a text node has no `closest`.
      const anchor = target && target.closest ? target.closest('a[href]') : null;
      if (!anchor) return;
      // `.href` on an anchor is already absolute, but an `<a>` inside an SVG
      // hands back an object rather than a string, and a `<base>` tag moves
      // what a relative one resolves to. `new URL(value, here)` is correct for
      // all three and throws on the one that is not a URL at all — which the
      // catch below is for.
      const hit = linkHit(
        new URL(String((anchor as HTMLAnchorElement).href), win.location.href),
        win.location,
      );
      if (hit) onHit(hit);
    } catch {
      /* never into the host page — see this file's header */
    }
  };

  doc.addEventListener('click', handle, true);
  doc.addEventListener('auxclick', handle, true);

  return (): void => {
    doc.removeEventListener('click', handle, true);
    doc.removeEventListener('auxclick', handle, true);
  };
}

/**
 * Did the server answer this document with a 404?
 *
 * ## The browser can be asked, and until recently it could not
 *
 * A script inside a page has never been able to see the status code that
 * delivered it, which is why every analytics product in this category asks the
 * customer to edit their 404 template. `PerformanceNavigationTiming`
 * `responseStatus` changes that: it is the status of the navigation request for
 * this very document, same-origin by definition, and reading it costs one
 * property access and no configuration from the customer at all.
 *
 * **It is not universal, and pretending otherwise would be the bug.** It is a
 * Chromium API; engines that do not implement it leave the property
 * `undefined`, and this returns false there rather than guessing. The
 * consequence belongs on the screen and not only in this comment: the 404 table
 * is a sample of real traffic, not a census, and the dashboard says so. A
 * sampled list of broken URLs is still the difference between knowing a
 * partner's link is dead and not knowing — and the alternative on offer, asking
 * every customer to paste a second snippet into a template most of them have
 * never opened, is the feature nobody installs.
 *
 * Only exact `404`. A 500 is an outage the customer's own monitoring should be
 * shouting about, and putting it in a table headed "broken links" would file an
 * incident under marketing. Widening this to a range is one character and a
 * deliberate decision, not an oversight.
 */
export function notFound(win: Window): boolean {
  try {
    const perf = win.performance as Performance | undefined;
    if (!perf || typeof perf.getEntriesByType !== 'function') return false;
    const entry = perf.getEntriesByType('navigation')[0] as
      | (PerformanceEntry & { responseStatus?: number })
      | undefined;
    return entry !== undefined && entry.responseStatus === 404;
  } catch {
    // An engine that throws on an unknown entry type has not told us the page
    // was fine — it has told us nothing, and nothing is not a 404.
    return false;
  }
}
